import { localStorage } from '../../../../../shared/localstorage';

/**
 * local storageのキー
 */
const KEY = 'YAHOO.JP.News.Top.newsFeedLocalTab.PC';
const VERSION = 1;

/**
 * localstorageに保存するデータのinterface
 */
interface IFeedTabStorage {
  // 関東, 東北といった地方
  region?: string;
  // 東京都など
  prefecture: IPrefectureStore;
}

export interface IPrefectureStore {
  [region: string]: string | undefined;
}

const actionTypes = {
  CHANGE_TAB: 'newsFeedLocalTab/CHANGE_TAB',
  CHANGE_PARENT_TAB: 'newsFeedLocalTab/CHANGE_PARENT_TAB',
  REFLECT_LOCALSTORAGE: 'newsFeedLocalTab/REFLECT_LOCALSTORAGE'
} as const;

/**
 * タブ切り替え
 *
 * @param region
 * @param prefecture
 */
const changeTab = (region: string, prefecture: string) => {
  return {
    type: actionTypes.CHANGE_TAB,
    payload: {
      region,
      prefecture
    }
  };
};

/**
 * 親タブ切り替え
 *
 * @param region
 */
const changeParentTab = (region: string) => {
  return {
    type: actionTypes.CHANGE_PARENT_TAB,
    payload: {
      region
    }
  };
};

/**
 * localstorageのデータ反映
 */
const reflectLocalStorage = {
  type: actionTypes.REFLECT_LOCALSTORAGE
} as const;

export const actions = {
  changeTab,
  changeParentTab,
  reflectLocalStorage
} as const;

type ReturnValueOrItself<T> = T extends (...args: infer _) => infer R ? R : T;

/**
 * Actionの型
 */
export type Action = ReturnValueOrItself<(typeof actions)[keyof typeof actions]>;

export interface IRegion {
  name: string;
  id: string;
  prefectures: IPrefecture[];
}

interface IPrefecture {
  name: string;
  id: string;
}

export interface INewsFeedLocalTabState {
  currentRegion?: IRegion;
  currentPrefecture?: IPrefecture;
  prefectureStore?: IPrefectureStore;
  isReflectedStorage?: boolean;
  regions: IRegion[];
}

const initialState: INewsFeedLocalTabState = {
  regions: []
};

export default (state: INewsFeedLocalTabState = initialState, action: Action) => {
  switch (action.type) {
    case actionTypes.CHANGE_TAB: {
      // インフィード広告インスタンスを初期化
      delete window.ytManager;
      // 都道府県を切り替える
      const { region, prefecture } = action.payload;

      // 選択中の都道府県を保存する
      const data: IFeedTabStorage = localStorage.get(KEY, VERSION) || {
        prefecture: {}
      };

      const newData = {
        ...data,
        region
      };

      newData.prefecture[region] = prefecture;
      localStorage.set(KEY, VERSION, newData);

      const currentRegion = pickCurrentRegion(state.regions, region);
      const currentPrefecture = currentRegion ? pickCurrentPrefecture(currentRegion, prefecture) : undefined;

      return {
        ...state,
        currentRegion,
        currentPrefecture
      };
    }
    case actionTypes.CHANGE_PARENT_TAB: {
      // インフィード広告インスタンスを初期化
      delete window.ytManager;
      // 地方を切り替える
      const data: IFeedTabStorage = localStorage.get(KEY, VERSION) || {
        prefecture: {}
      };

      const regionId = action.payload.region;
      const prefectureId: string | undefined = data ? data.prefecture[regionId] : undefined;

      const newData = {
        ...data,
        region: regionId
      };

      const currentRegion = pickCurrentRegion(state.regions, regionId);
      const currentPrefecture = currentRegion ? pickCurrentPrefecture(currentRegion, prefectureId) : undefined;

      // 選択中の地方をstorageに保存する
      localStorage.set(KEY, VERSION, newData);

      return {
        ...state,
        currentRegion,
        currentPrefecture,
        prefectureStore: newData.prefecture
      };
    }
    case actionTypes.REFLECT_LOCALSTORAGE: {
      // localstorageのデータを取得してstoreに反映
      const data: IFeedTabStorage = localStorage.get(KEY, VERSION) || {
        prefecture: {}
      };

      if (!data) {
        return state;
      }

      const regionId = data.region;
      const prefectureId = regionId ? data.prefecture[regionId] : undefined;
      const currentRegion = pickCurrentRegion(state.regions, regionId);
      const currentPrefecture = currentRegion ? pickCurrentPrefecture(currentRegion, prefectureId) : undefined;

      return {
        ...state,
        isReflectedStorage: true,
        currentRegion,
        currentPrefecture,
        prefectureStore: data.prefecture
      };
    }
  }
  return state;
};

/**
 * 選択中のregionを返す 現在選択しているidがない場合は先頭要素を返す
 *
 * @param regions
 * @param currentRegionId
 */
function pickCurrentRegion(regions: IRegion[], currentRegionId?: string): IRegion | undefined {
  return regions.find((region, index) => (currentRegionId ? region.id === currentRegionId : index === 0));
}

/**
 * 指定したregionの選択されている都道府県を返す
 *
 * @param region
 * @param currentPrefecture
 */
function pickCurrentPrefecture(region: IRegion, currentPrefecture?: string): IPrefecture | undefined {
  return region.prefectures.find((prefecture, index) =>
    currentPrefecture ? prefecture.id === currentPrefecture : index === 0
  );
}
